import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  notification,
  Grid,
  Divider,
  Upload,
  Checkbox,
  message,
} from 'antd';
import * as R from 'ramda';
import moment from 'moment';

import { CREATE_COMPLAINT_QUERY, UPLOAD_QUERY } from 'queries';

export interface ComplaintFormPageProps {
  copy: any;
}

export default function ComplaintFormPage({ copy }) {
  const screens = Grid.useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const [createComplaint] = useMutation(CREATE_COMPLAINT_QUERY, {
    async onCompleted(data) {
      try {
        if (form.getFieldValue('powerOfAttorney')?.length) {
          await uploadDocument({
            variables: {
              refId: data.createComplaint.data.id,
              ref: 'api::complaint.complaint',
              field: 'powerOfAttorney',
              file: form.getFieldValue('powerOfAttorney')[0].originFileObj,
            },
          });
        }

        if (form.getFieldValue('contractualDocuments')?.length) {
          await uploadDocument({
            variables: {
              refId: data.createComplaint.data.id,
              ref: 'api::complaint.complaint',
              field: 'contractualDocuments',
              file: form.getFieldValue('contractualDocuments')[0].originFileObj,
            },
          });
        }

        if (form.getFieldValue('otherDocuments')?.length) {
          await uploadDocument({
            variables: {
              refId: data.createComplaint.data.id,
              ref: 'api::complaint.complaint',
              field: 'otherDocuments',
              file: form.getFieldValue('otherDocuments')[0].originFileObj,
            },
          });
        }

        notification.success({
          message: 'Complaint created',
          description:
            'Your complaint has been successfully created. You will hear from us no later than 10 business days from now, although we strive to respond within 3 business days. Thank you.',
        });

        form.resetFields();
      } catch (error) {
        console.log('Error submitting complaint:', error);
        message.error(
          'There was an error submitting your complaint, please try again.'
        );
      } finally {
        setLoading(false);
      }
    },
    onError(error) {
      console.log('Error submitting complaint:', error);
      message.error(
        'There was an error submitting your complaint, please try again.'
      );
    },
  });

  const [uploadDocument] = useMutation(UPLOAD_QUERY);

  const onFormFinish = (values) => {
    setLoading(true);

    const formData = R.omit(
      ['powerOfAttorney', 'contractualDocuments', 'otherDocuments'],
      values
    );

    createComplaint({
      variables: {
        ...formData,
        dateCreated: moment().format(),
      },
    });
  };

  const basicFormRules = [
    {
      required: true,
      message: 'This is a required field',
    },
  ];

  const ContactFields = ({
    field,
    showRegAndLEI = false,
    required = false,
  }) => (
    <>
      <Row gutter={20}>
        <Col xs={24} md={12}>
          <Form.Item
            name={[field, 'lastName']}
            label={copy.form.contactFields.lastName}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name={[field, 'firstName']}
            label={copy.form.contactFields.firstName}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      {showRegAndLEI && (
        <Row>
          <Col span={24}>
            <Form.Item
              name={[field, 'registrationNumberAndLEI']}
              label={copy.form.contactFields.regAndLEI}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row>
        <Col span={24}>
          <Form.Item
            name={[field, 'address']}
            label={copy.form.contactFields.address}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={20}>
        <Col xs={24} md={8}>
          <Form.Item
            name={[field, 'postcode']}
            label={copy.form.contactFields.postcode}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name={[field, 'city']}
            label={copy.form.contactFields.city}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name={[field, 'country']}
            label={copy.form.contactFields.country}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={20}>
        <Col xs={24} md={12}>
          <Form.Item
            name={[field, 'telephone']}
            label={copy.form.contactFields.telephone}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name={[field, 'email']}
            label={copy.form.contactFields.email}
            rules={required ? basicFormRules : []}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  return (
    <>
      <Divider style={{ margin: '40px 0 0' }} />
      <Typography.Title level={2} style={{ margin: '30px 0' }}>
        {copy.form.title}
      </Typography.Title>
      <Form form={form} onFinish={onFormFinish} layout="vertical">
        <Typography.Title level={5} style={{ marginBottom: 20 }}>
          {copy.form.personalDataComplainant}
        </Typography.Title>
        <ContactFields
          field="personalDataOfTheComplainant"
          showRegAndLEI
          required
        />
        <Divider />
        <Typography.Title level={5} style={{ marginBottom: 20 }}>
          {copy.form.contactDataComplainant}
        </Typography.Title>
        <ContactFields field="contactDetailsOfComplainant" />
        <Divider />
        <Typography.Title level={5} style={{ marginBottom: 20 }}>
          {copy.form.personalDataLegalRep}
        </Typography.Title>
        <ContactFields
          field="personalDataOfTheLegalRepresentative"
          showRegAndLEI
        />
        <Divider />
        <Typography.Title level={5} style={{ marginBottom: 20 }}>
          {copy.form.contactDataLegalRep}
        </Typography.Title>
        <ContactFields field="contactDetailsOfLegalRepresentative" />
        <Divider />
        <Typography.Title level={5} style={{ marginBottom: 30 }}>
          {copy.form.section3.title}:
        </Typography.Title>
        <Row>
          <Col span={24}>
            <Form.Item
              name="referenceOfInvestmentOrAgreement"
              label={copy.form.section3.a}
              rules={basicFormRules}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Row>
          <Col span={24}>
            <Form.Item
              name="complaintSubjectMatter"
              label={copy.form.section3.b}
              rules={basicFormRules}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Row>
          <Col span={24}>
            <Form.Item
              name="datesOfFacts"
              label={copy.form.section3.c}
              rules={basicFormRules}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Row>
          <Col span={24}>
            <Form.Item name="descriptionOfDamage" label={copy.form.section3.d}>
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Row>
          <Col span={24}>
            <Form.Item name="otherInformation" label={copy.form.section3.e}>
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Typography.Title level={5} style={{ marginBottom: 20 }}>
          {copy.form.documentation.subtitle}
        </Typography.Title>
        <Row>
          <Col span={24}>
            <Form.Item
              name="powerOfAttorney"
              label={copy.form.documentation.field1}
            >
              <Upload
                action={'/api/noop'}
                onChange={({ fileList: newFileList }) => {
                  form.setFieldsValue({ powerOfAttorney: newFileList });
                }}
              >
                <Button>Choose file</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Row>
          <Col span={24}>
            <Form.Item
              name="contractualDocuments"
              label={copy.form.documentation.field2}
            >
              <Upload
                action={'/api/noop'}
                onChange={({ fileList: newFileList }) => {
                  form.setFieldsValue({ contractualDocuments: newFileList });
                }}
              >
                <Button>Choose file</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Row>
          <Col span={24}>
            <Form.Item
              name="otherDocuments"
              label={copy.form.documentation.field3}
            >
              <Upload
                action={'/api/noop'}
                onChange={({ fileList: newFileList }) => {
                  form.setFieldsValue({ otherDocuments: newFileList });
                }}
              >
                <Button>Choose file</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 10 }} />
        <Form.Item
          style={{ marginBottom: 30 }}
          name="commsAccepted"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        'It is required for you to accept this agreement'
                      )
                    ),
            },
          ]}
        >
          <Checkbox>{copy.form.commsAgreement}</Checkbox>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit complaint
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}
